import { useId, useState, type ComponentPropsWithRef, type ReactNode } from "react";
import { useI18n } from "@wato/i18n/react";
import { cx } from "../../cx";
import { EyeIcon, EyeOffIcon } from "../../rtl/icons";

export type InputType =
  "text" | "number" | "password" | "email" | "search" | "tel" | "url" | "datetime-local" | "date";

/** Field colours — semantic tokens only (pairs verified by test/contrast.test.ts). */
export const inputStateClasses = {
  base: "bg-surface text-fg placeholder:text-fg-subtle border-line-strong",
  error: "bg-surface text-fg placeholder:text-fg-subtle border-danger-line",
} as const;

export interface InputProps extends Omit<ComponentPropsWithRef<"input">, "type" | "size"> {
  readonly type?: InputType;
  /** Visible label (required for accessibility). */
  readonly label: string;
  /** Visually hide the label but keep it for screen readers. */
  readonly hideLabel?: boolean;
  readonly hint?: string;
  /** Error message; also sets `aria-invalid`. */
  readonly error?: string;
  /** Icon at the inline start (right in RTL). */
  readonly startIcon?: ReactNode;
  /** Icon at the inline end (left in RTL). Ignored for `password` (visibility toggle). */
  readonly endIcon?: ReactNode;
  /**
   * Direction of the field only (e.g. `"ltr"` for phone numbers in an Arabic form).
   * Applied to the field wrapper so icons and padding flip together with the text.
   */
  readonly dir?: "ltr" | "rtl" | "auto";
  readonly containerClassName?: string;
}

export function Input({
  type = "text",
  label,
  hideLabel = false,
  hint,
  error,
  startIcon,
  endIcon,
  dir,
  id,
  className,
  containerClassName,
  disabled,
  ...rest
}: InputProps) {
  const { t } = useI18n();
  const autoId = useId();
  const inputId = id ?? `input-${autoId}`;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(" ") || undefined;

  const [revealed, setRevealed] = useState(false);
  const isPassword = type === "password";
  const effectiveType = isPassword && revealed ? "text" : type;
  const end: ReactNode = isPassword ? (
    <button
      type="button"
      onClick={() => setRevealed((r) => !r)}
      disabled={disabled}
      aria-pressed={revealed}
      aria-controls={inputId}
      aria-label={t(revealed ? "ui.input.hidePassword" : "ui.input.showPassword")}
      className="pointer-events-auto flex size-8 items-center justify-center rounded-sm text-fg-muted hover:text-fg focus-visible:outline-2 focus-visible:outline-focus"
    >
      {revealed ? <EyeOffIcon className="size-4" /> : <EyeIcon className="size-4" />}
    </button>
  ) : (
    endIcon
  );

  return (
    <div className={cx("flex flex-col gap-1.5", containerClassName)}>
      <label
        htmlFor={inputId}
        className={cx("text-sm font-medium text-fg", hideLabel && "sr-only")}
      >
        {label}
      </label>
      <div className="relative" dir={dir}>
        {startIcon && (
          <span className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3 text-fg-muted">
            {startIcon}
          </span>
        )}
        <input
          id={inputId}
          type={effectiveType}
          inputMode={type === "number" ? "decimal" : undefined}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cx(
            "block h-11 w-full rounded-xl border text-sm transition-colors",
            "focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-focus",
            "disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:opacity-70",
            error ? inputStateClasses.error : inputStateClasses.base,
            startIcon ? "ps-10" : "ps-3",
            end ? "pe-10" : "pe-3",
            className,
          )}
          {...rest}
        />
        {end && (
          <span
            className={cx(
              "absolute inset-y-0 end-0 flex items-center text-fg-muted",
              isPassword ? "pe-1" : "pointer-events-none pe-3",
            )}
          >
            {end}
          </span>
        )}
      </div>
      {error && (
        <p id={errorId} className="text-xs text-danger-fg">
          {error}
        </p>
      )}
      {hint && (
        <p id={hintId} className="text-xs text-fg-muted">
          {hint}
        </p>
      )}
    </div>
  );
}
